// Error recovery utilities with retry logic

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  exponential?: boolean;
}

export class RetryableError extends Error {
  constructor(message: string, public retryable: boolean = true) {
    super(message);
    this.name = 'RetryableError';
  }
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    exponential = true,
  } = options;

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');

      // Don't retry on non-retryable errors
      if (error instanceof RetryableError && !error.retryable) {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      const delay = exponential
        ? Math.min(baseDelay * Math.pow(2, attempt), maxDelay)
        : baseDelay;

      // Add jitter to prevent thundering herd
      const jitter = Math.random() * 0.3 * delay;
      await new Promise((resolve) => setTimeout(resolve, delay + jitter));
    }
  }

  throw lastError!;
}

export function isNetworkError(error: Error): boolean {
  return (
    error.message.includes('fetch') ||
    error.message.includes('network') ||
    error.message.includes('offline') ||
    error.message.includes('Failed to fetch')
  );
}

export function getUserFriendlyErrorMessage(error: Error): string {
  if (isNetworkError(error)) {
    return 'Connection issue. Please check your internet and try again.';
  }

  if (error.message.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  if (error.message.includes('unauthorized') || error.message.includes('401')) {
    return 'Session expired. Please log in again.';
  }

  if (error.message.includes('forbidden') || error.message.includes('403')) {
    return 'You don\'t have permission to perform this action.';
  }

  if (error.message.includes('not found') || error.message.includes('404')) {
    return 'The requested resource was not found.';
  }

  if (error.message.includes('server') || error.message.includes('500')) {
    return 'Server error. Our team has been notified.';
  }

  return error.message || 'Something went wrong. Please try again.';
}

export interface RecoveryAction {
  label: string;
  action: () => void;
  primary?: boolean;
}

export function getRecoveryActions(error: Error): RecoveryAction[] {
  const actions: RecoveryAction[] = [];

  if (isNetworkError(error)) {
    actions.push({
      label: 'Retry',
      action: () => window.location.reload(),
      primary: true,
    });
  }

  if (error.message.includes('unauthorized')) {
    actions.push({
      label: 'Log in',
      action: () => (window.location.href = '/auth'),
      primary: true,
    });
  }

  // Always provide a way to go back or contact support
  actions.push({
    label: 'Go Home',
    action: () => (window.location.href = '/'),
  });

  actions.push({
    label: 'Contact Support',
    action: () => (window.location.href = '/help-center'),
  });

  return actions;
}
